import { Button } from "@/components/ui/button";
import type { LaneSummaryData } from "@/lib/setup/lanes";

/** Board C's summary block: what the ticked seeds bring in, split by quick-check
 *  band, and the one seed most worth unticking. */
export function LaneSummary({ ticked, summary, drag, onUntick, problem, onRetry }: {
  ticked: number;
  /** null: nothing ticked (or checked) yet — the box holds its place with a hint. */
  summary: LaneSummaryData | null;
  drag: { label: string; n: number; on: number } | null;
  onUntick: () => void;
  /** Some papers went unchecked: a request failed (Retry) or today's limit was hit. */
  problem?: "failed" | "capped";
  onRetry?: () => void;
}) {
  if (!summary) {
    return (
      <section aria-label="What your ticked seeds bring in" className="flex flex-col gap-2.5 rounded-lg border bg-card p-4">
        <span className="font-medium">Tick seeds to see what they bring in</span>
        <div className="h-2.5 rounded-full bg-muted" />
        <p aria-hidden className="min-h-5" />
        <Footnote problem={problem} onRetry={onRetry} />
      </section>
    );
  }
  const { total, on, partly, off } = summary;
  const pct = (v: number) => `${total ? (v / total) * 100 : 0}%`;
  const legend = [
    { v: on, label: "on your question", bg: "bg-green-400" },
    { v: partly, label: "partly", bg: "bg-yellow-400" },
    { v: off, label: "off it", bg: "bg-muted-foreground/50" },
  ];
  return (
    <section aria-label="What your ticked seeds bring in" className="flex flex-col gap-2.5 rounded-lg border bg-card p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1.5">
        <span className="font-medium">
          {ticked === 1 ? "Your ticked seed brings" : `Your ${ticked} ticked seeds bring`} in about {total} papers
        </span>
        <span className="flex gap-3.5 text-xs text-muted-foreground tabular-nums">
          {legend.map((l) => (
            <span key={l.label} className="inline-flex items-center gap-1.5">
              <span aria-hidden className={`size-2 rounded-sm ${l.bg}`} />{`${l.v} ${l.label}`}
            </span>
          ))}
        </span>
      </div>
      <div role="img" aria-label={`${on} on your question, ${partly} partly, ${off} off it`}
        className="flex h-2.5 overflow-hidden rounded-full bg-muted">
        {legend.map((l) => <div key={l.label} className={l.bg} style={{ width: pct(l.v) }} />)}
      </div>
      {/* The drag line's row is always there, so it coming and going doesn't move the list. */}
      <p className="flex min-h-5 flex-wrap items-center gap-2 text-sm text-muted-foreground">
        {drag && (<>
          <span>
            <span className="font-medium text-foreground">{drag.label}</span>
            {` brings about ${drag.n} papers but only ${drag.on} on your question.`}
          </span>
          <Button variant="link" className="h-auto p-0 text-sm" onClick={onUntick}>Untick it</Button>
        </>)}
      </p>
      <Footnote problem={problem} onRetry={onRetry} />
    </section>
  );
}

/** The footnote line doubles as the quick check's error line, so an error
 *  arriving doesn't move the seed list under the pointer. */
function Footnote({ problem, onRetry }: { problem?: "failed" | "capped"; onRetry?: () => void }) {
  if (problem === "failed") {
    return (
      <p className="text-xs text-muted-foreground">
        Some papers couldn't be checked ·{" "}
        <Button variant="link" className="h-auto p-0 text-xs" onClick={onRetry}>Retry</Button>
      </p>
    );
  }
  if (problem === "capped") {
    return <p className="text-xs text-muted-foreground">Quick check limit reached for today — some papers are unchecked.</p>;
  }
  return <p className="text-xs text-muted-foreground">Quick check by AI on titles and abstracts — the real grading is in the next step.</p>;
}
